import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import styles from './ConversationalMode.module.scss'
import ConversationWindow from './ConversationWindow/ConversationWindow.tsx'
import ConversationInput, { type ConversationInputHandle } from './ConversationInput/ConversationInput.tsx'
import ChatSearchModal from './ChatSearchModal/ChatSearchModal.tsx'
import ConversationHistorySidebar from './ConversationHistorySidebar/ConversationHistorySidebar.tsx'
import { Message, ConversationSession } from '../../../../utils/conversationStorage.ts'
import { getDesktopSuggestions, getPostFlowSuggestions } from '../dashboardActionsConfig.tsx'
import type { DesktopSuggestion } from '../dashboardActionsConfig.tsx'
import {
  ProjectAutofillDraftSendInterstitial,
  useProjectAutofillDraftSendGate
} from './ProjectAutofillDraftSendInterstitial.tsx'
import { useProjectAutofillDraftGuard } from '../../../../contexts/ProjectAutofillDraftGuardContext.tsx'
import type { DocumentPipelineStreamHint } from '../../../../contexts/ConversationalAgentContext.tsx'
import { pendingAttachmentFallbackCaption } from '../../../../utils/agentImageStorage.ts'
import {
  isProjectAutofillAgentId,
  SUGGESTED_ACTION_ID_TO_AGENT,
  suggestedChipSkipsActiveAgentOverride
} from '../../../../constants/agentNames.ts'

interface ConversationalModeProps {
  isActive: boolean
  isMobile?: boolean
  messages: Message[]
  onSendMessage: (
    message: string,
    options?: { confirmExecution?: boolean; activeAgentOverride?: string }
  ) => Promise<void>
  onClose: () => void
  isLoading?: boolean
  canSendMessage: boolean
  remainingMessages: number
  hasReachedLimit: boolean
  limit: number
  matchedSuggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  onRequestGuide?: () => void
  allSessions: ConversationSession[]
  currentSessionId: string | null
  onSelectSession: (sessionId: string) => void
  onCreateNewSession: () => void
  onDeleteSession: (sessionId: string) => void
  userId?: string
  userData?: any
  searchModalRef?: React.MutableRefObject<(() => void) | null>
  onSidebarStateChange?: (isCollapsed: boolean) => void
  missingFields?: string[]
  readyToExecute?: boolean
  showImageUpload?: boolean
  allowDocuments?: boolean
  collectedData?: { intent?: string; draftId?: string; nextAction?: string }
  hasPendingImage?: boolean
  imageUploadAcknowledged?: boolean
  pendingImagePreview?: string | null
  pendingAttachmentMime?: string | null
  pendingAttachmentFileName?: string | null
  pendingAttachmentSizeBytes?: number | null
  lastAnalyzedDocumentationFileName?: string | null
  onImageSelect?: (file: File) => void
  onImageClear?: () => void
  onConfirmExecution?: () => void
  showDocumentPipelineLoop?: boolean
  documentPipelinePhase?: string | null
  documentPipelineStreamHint?: DocumentPipelineStreamHint
  documentPipelinePrimaryFileOriginalName?: string | null
  documentPipelinePrimaryFileSizeBytes?: number | null
}

const ConversationalMode: React.FC<ConversationalModeProps> = ({
  isActive,
  isMobile = false,
  messages,
  onSendMessage,
  onClose,
  isLoading = false,
  canSendMessage,
  remainingMessages,
  hasReachedLimit,
  limit,
  matchedSuggestion,
  onSuggestionAction,
  onRequestGuide,
  allSessions,
  currentSessionId,
  onSelectSession,
  onCreateNewSession,
  onDeleteSession,
  userId,
  userData,
  searchModalRef,
  onSidebarStateChange,
  missingFields = [],
  readyToExecute = false,
  showImageUpload: showImageUploadProp,
  allowDocuments = false,
  collectedData,
  hasPendingImage = false,
  imageUploadAcknowledged = false,
  pendingImagePreview = null,
  pendingAttachmentMime = null,
  pendingAttachmentFileName = null,
  pendingAttachmentSizeBytes = null,
  lastAnalyzedDocumentationFileName = null,
  onImageSelect,
  onImageClear,
  onConfirmExecution,
  showDocumentPipelineLoop = false,
  documentPipelinePhase = null,
  documentPipelineStreamHint = null,
  documentPipelinePrimaryFileOriginalName = null,
  documentPipelinePrimaryFileSizeBytes = null
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [inputValue, setInputValue] = useState('')
  const attachmentPickerRef = useRef<ConversationInputHandle>(null)
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const draftSendGate = useProjectAutofillDraftSendGate()
  const { draftUiTick, getProjectAutofillDraftGuardApi } = useProjectAutofillDraftGuard()

  useEffect(() => {
    if (isActive) {
      setIsSidebarCollapsed(false)
      onSidebarStateChange?.(false)
    }
  }, [isActive, onSidebarStateChange])

  const handleToggleSidebar = () => {
    const newState = !isSidebarCollapsed
    setIsSidebarCollapsed(newState)
    onSidebarStateChange?.(newState)
  }

  const allSuggestions = useMemo(() => {
    const base = getDesktopSuggestions(navigate, userId, userData, dispatch)
    const postFlow = getPostFlowSuggestions(onConfirmExecution ?? undefined)
    return [...base, ...postFlow]
  }, [navigate, userId, userData, dispatch, onConfirmExecution])

  const openSearchModal = () => {
    setIsSearchModalOpen(true)
  }

  useEffect(() => {
    if (searchModalRef) {
      searchModalRef.current = openSearchModal
      return () => {
        searchModalRef.current = null
      }
    }
  }, [searchModalRef])

  const isProjectAutofillFlow = useMemo(() => {
    const latestAssistantMessage = [...messages].reverse().find((m) => m.role === 'assistant')
    let latestActiveAgentFromLLM: string | null = null
    if (latestAssistantMessage?.llmRawResponse) {
      try {
        latestActiveAgentFromLLM = JSON.parse(latestAssistantMessage.llmRawResponse)?.activeAgent || null
      } catch {
        latestActiveAgentFromLLM = null
      }
    }
    return (
      collectedData?.intent === 'project_autofill' ||
      latestAssistantMessage?.actionId === 'project-autofill' ||
      isProjectAutofillAgentId(latestActiveAgentFromLLM)
    )
  }, [messages, collectedData?.intent])

  const uploadDocsFromLastAssistant = useMemo(() => {
    const m = [...messages].reverse().find((x) => x.role === 'assistant')
    if (!m?.llmRawResponse) return false
    try {
      const o = JSON.parse(m.llmRawResponse) as { nextAction?: string }
      return o?.nextAction === 'upload_docs'
    } catch {
      return false
    }
  }, [messages])

  const highlightProjectAutofillAttachment = useMemo(
    () =>
      isProjectAutofillFlow &&
      !collectedData?.draftId &&
      (collectedData?.nextAction === 'upload_docs' || uploadDocsFromLastAssistant),
    [
      isProjectAutofillFlow,
      collectedData?.draftId,
      collectedData?.nextAction,
      uploadDocsFromLastAssistant
    ]
  )

  const draftPublishInput = useMemo(() => {
    if (!isProjectAutofillFlow) {
      return { show: false, publishing: false }
    }
    void draftUiTick
    const api = getProjectAutofillDraftGuardApi()
    if (!api?.publishDraft) {
      return { show: false, publishing: false }
    }
    const publishing = api.isPublishing?.() ?? false
    const can = api.canPublish?.() ?? false
    const show = can || publishing
    return { show, publishing }
  }, [draftUiTick, getProjectAutofillDraftGuardApi, isProjectAutofillFlow])

  const handlePublishDraftFromInput = useCallback(async () => {
    const api = getProjectAutofillDraftGuardApi()
    if (!api?.publishDraft) return
    await api.publishDraft()
  }, [getProjectAutofillDraftGuardApi])

  const handleSuggestedChipSend = useCallback(
    (label: string, actionId: string) => {
      if (draftSendGate.tryHoldSend(label)) {
        return
      }
      const override =
        suggestedChipSkipsActiveAgentOverride(actionId)
          ? undefined
          : SUGGESTED_ACTION_ID_TO_AGENT[actionId]
      void onSendMessage(label, override ? { activeAgentOverride: override } : undefined)
    },
    [draftSendGate, onSendMessage]
  )

  const requestOpenAttachmentPicker = useCallback(() => {
    attachmentPickerRef.current?.openAttachmentPicker()
  }, [])

  if (!isActive) {
    return null
  }

  const handleSend = async () => {
    if ((!inputValue.trim() && !hasPendingImage) || isLoading || !canSendMessage) {
      return
    }

    const payload =
      inputValue.trim() ||
      (hasPendingImage ? pendingAttachmentFallbackCaption(pendingAttachmentMime) : '')
    const gateHeld = draftSendGate.tryHoldSend(payload)
    if (gateHeld) {
      return
    }

    setInputValue('')
    await onSendMessage(payload)
  }

  const handleConfirmExecution = async () => {
    if (isLoading) return
    setInputValue('')
    await onConfirmExecution?.()
  }

  const showImageUpload =
    !!showImageUploadProp ||
    missingFields.includes('image_uploaded') ||
    isProjectAutofillFlow ||
    (readyToExecute && collectedData?.intent === 'create_post')

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const rootClassName = [
    styles.conversationalMode,
    isMobile ? styles.conversationalModeMobile : styles.conversationalModeDesktop,
    !isMobile && isSidebarCollapsed ? styles.sidebarCollapsed : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClassName} data-desktop={!isMobile || undefined}>
      {!isMobile && (
        <ConversationHistorySidebar
          sessions={allSessions}
          currentSessionId={currentSessionId}
          onSelectSession={onSelectSession}
          onCreateNewSession={onCreateNewSession}
          onDeleteSession={onDeleteSession}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          userId={userId}
          userData={userData}
          onOpenSearchModal={openSearchModal}
        />
      )}
      <div className={styles.conversationContent}>
        <div className={styles.conversationMain}>
          <ConversationWindow
            messages={messages}
            onClose={onClose}
            isLoading={isLoading}
            matchedSuggestion={matchedSuggestion}
            onSuggestionAction={onSuggestionAction}
            onRequestGuide={
              onRequestGuide
                ? () => {
                    const guideMsg = 'Ayudame a hacerlo'
                    if (draftSendGate.tryHoldSend(guideMsg)) {
                      return
                    }
                    onRequestGuide()
                  }
                : undefined
            }
            allSuggestions={allSuggestions}
            hasReachedLimit={hasReachedLimit}
            remainingMessages={remainingMessages}
            limit={limit}
            userData={userData}
            pendingImagePreview={pendingImagePreview}
            isLatestMessageGuardarPost={messages[messages.length - 1]?.actionId === 'guardar-post'}
            showDocumentPipelineLoop={showDocumentPipelineLoop}
            documentPipelinePhase={documentPipelinePhase}
            documentPipelineStreamHint={documentPipelineStreamHint}
            documentPipelinePrimaryFileOriginalName={documentPipelinePrimaryFileOriginalName}
            documentPipelinePrimaryFileSizeBytes={documentPipelinePrimaryFileSizeBytes}
            onSuggestedChipSend={handleSuggestedChipSend}
            onRequestOpenAttachmentPicker={requestOpenAttachmentPicker}
          />
        </div>
        <div className={styles.floatingInputContainer}>
          <ConversationInput
            ref={attachmentPickerRef}
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            onKeyPress={handleKeyPress}
            disabled={isLoading || !canSendMessage}
            placeholder={
              canSendMessage
                ? highlightProjectAutofillAttachment && !hasPendingImage
                  ? 'Podés seguir escribiendo o adjuntar un archivo para el borrador…'
                  : 'Escribí tu mensaje...'
                : 'Has alcanzado el límite de mensajes'
            }
            showImageUpload={showImageUpload}
            allowVideo={collectedData?.intent === 'create_post'}
            allowDocuments={allowDocuments || isProjectAutofillFlow}
            highlightProjectAutofillAttachment={highlightProjectAutofillAttachment}
            hasPendingImage={hasPendingImage}
            imageUploadAcknowledged={imageUploadAcknowledged}
            pendingImagePreview={pendingImagePreview}
            pendingAttachmentMime={pendingAttachmentMime}
            pendingAttachmentFileName={pendingAttachmentFileName}
            pendingAttachmentSizeBytes={pendingAttachmentSizeBytes}
            lastAnalyzedDocumentationFileName={lastAnalyzedDocumentationFileName}
            onImageSelect={onImageSelect}
            onImageClear={onImageClear}
            readyToExecute={readyToExecute}
            onConfirmExecution={readyToExecute ? handleConfirmExecution : undefined}
            showDraftPublish={draftPublishInput.show}
            draftPublishing={draftPublishInput.publishing}
            onPublishDraft={draftPublishInput.show ? handlePublishDraftFromInput : undefined}
            onOpenHistory={isMobile ? openSearchModal : undefined}
            historyCount={isMobile ? allSessions.length : undefined}
          />
        </div>
      </div>
      <ProjectAutofillDraftSendInterstitial
        open={draftSendGate.heldPayload != null}
        payload={draftSendGate.heldPayload ?? ''}
        getProjectAutofillDraftGuardApi={draftSendGate.getProjectAutofillDraftGuardApi}
        onClearInputAndDismissGate={() => {
          setInputValue('')
          draftSendGate.dismiss()
        }}
        onSendMessage={onSendMessage}
        onCancel={draftSendGate.dismiss}
      />
      <ChatSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        sessions={allSessions}
        currentSessionId={currentSessionId}
        onSelectSession={onSelectSession}
        onCreateNewSession={onCreateNewSession}
        onDeleteSession={onDeleteSession}
        userId={userId}
        userData={userData}
      />
    </div>
  )
}

export default ConversationalMode
